import { Events, User } from '@carbon/react/icons';
import { Date } from 'components/date';
import { LaunchLink } from 'components/launch-link';
import { Tag } from '@carbon/react';
import { ResourceFetcher } from 'components/resource-fetcher';
import { AliasedClusterResponse, EntityResponse } from 'types/api-responses';
import { DefinitionEntry } from './definition-entry';

import style from './entity-preview.module.scss';

// The /entities/:id/aliased_entities endpoint returns an alias cluster
// ({ members, edges }); this card only needs the (non-canonical) members.
const AliasedEntities = ({ data }: { data: AliasedClusterResponse }) => {
  if (!data.members?.length) return null;
  const nonCanonical = data.members.filter((entity) => !entity.canonical);
  if (!nonCanonical.length) return null;
  return (
    <dl className={style.aliasesArea}>
      <dt>Aliases</dt>
      <dd>
        <ul>
          {nonCanonical.map(({ entity }) => (
            <li key={entity.id}>
              <LaunchLink
                to={entity.href}
                className={style.twoLinesTruncate}
                label={`${entity.name} — open entity in a new tab`}
              >
                {entity.name}
              </LaunchLink>
            </li>
          ))}
        </ul>
      </dd>
    </dl>
  );
};

const EntityPreviewContent = ({ entity }: { entity: EntityResponse }) => {
  const type = entity.type;
  return (
    <div>
      <div className={style.titleArea}>
        <div className={style.iconArea}>
          {type === 'OrganizationEntity' ? <Events size={20} /> : <User />}
        </div>
        <div className={style.popName}>
          {entity.name} <p>Entity</p>
        </div>
      </div>
      <DefinitionEntry label="Entity Id">
        <>{entity.id}</>
      </DefinitionEntry>
      <DefinitionEntry label="Entity Type">
        <Tag size="sm" type="cyan">
          {type.replace('Entity', '')}
        </Tag>
      </DefinitionEntry>
      {type === 'IndividualEntity' && (
        <DefinitionEntry label="Date of Birth">
          <Date date={entity.concreteEntity.birthDate} fallback="(none)" />
        </DefinitionEntry>
      )}
      {type === 'IndividualEntity' && (
        <DefinitionEntry label="Date of Death">
          <Date date={entity.concreteEntity.deathDate} fallback="(none)" />
        </DefinitionEntry>
      )}
      {type === 'OrganizationEntity' && (
        <DefinitionEntry label="Formation Date">
          <Date date={entity.concreteEntity.formationDate} fallback="(none)" />
        </DefinitionEntry>
      )}
      {type === 'OrganizationEntity' && (
        <DefinitionEntry label="Termination Date">
          <Date
            date={entity.concreteEntity.terminationDate}
            fallback="(none)"
          />
        </DefinitionEntry>
      )}
      <div>
        <ResourceFetcher<AliasedClusterResponse>
          href={entity.resources.aliasedEntities.href}
          component={AliasedEntities}
        />
      </div>
    </div>
  );
};

const EntityPreview = ({ data }: { data: EntityResponse }) => {
  return (
    <div>
      <EntityPreviewContent entity={data} />
    </div>
  );
};

export { EntityPreview, AliasedEntities };
